import React from "react";
import { Link } from "react-router-dom";
import {
  FiArrowRight,
  FiBarChart2,
  FiCheck,
  FiCompass,
  FiCpu,
  FiLayers,
  FiShield,
  FiUsers,
} from "react-icons/fi";
import { ImAirplane } from "react-icons/im";

import cityImage from "../../assets/images/city.jpg";
import peopleImage from "../../assets/images/whatwedo.jpg";
import insuranceLogo from "../../assets/logos/TIB.png";
import travelLogo from "../../assets/logos/TTT.png";
import "./Whatwedo.css";

const operatingModel = [
  {
    icon: FiCompass,
    title: "Strategic direction",
    text: "We help each business set a clear direction, prioritise opportunities and stay focused on long-term value.",
  },
  {
    icon: FiShield,
    title: "Governance & risk",
    text: "We promote responsible oversight, clear accountability and sound risk management across the group.",
  },
  {
    icon: FiUsers,
    title: "People & capability",
    text: "We strengthen leadership, professional skills and the service culture required for sustainable performance.",
  },
  {
    icon: FiCpu,
    title: "Technology & improvement",
    text: "We encourage practical technology and better processes that make our companies easier to work with.",
  },
];

const Whatwedo = () => {
  return (
    <main className="what-page">
      <section
        className="what-hero"
        style={{ "--what-hero-image": `url(${cityImage})` }}
        aria-labelledby="what-page-title"
      >
        <div className="what-shell what-hero__inner">
          <p className="what-eyebrow what-eyebrow--light">What we do</p>
          <h1 id="what-page-title">
            Turning specialist expertise into <span>everyday value.</span>
          </h1>
          <p>
            Thrive Holdings builds stronger operating companies by combining
            strategic oversight with the freedom and focus of specialist
            teams.
          </p>
          <a className="what-button what-button--primary" href="#our-model">
            How we create value <FiArrowRight aria-hidden="true" />
          </a>
        </div>
      </section>

      <section className="what-intro">
        <div className="what-shell what-intro__grid">
          <div className="what-heading">
            <p className="what-eyebrow">Our role</p>
            <h2>We create the conditions for good businesses to thrive.</h2>
          </div>
          <div className="what-intro__copy">
            <p>
              Our operating companies are closest to their customers. The
              holding company gives them the structure, perspective and shared
              capabilities needed to make better decisions and deliver
              consistently.
            </p>
            <p>
              This approach keeps expertise where it belongs—inside each
              specialist business—while creating a common standard for
              leadership, governance and growth.
            </p>
          </div>
        </div>
      </section>

      <section className="what-model" id="our-model">
        <div className="what-shell">
          <div className="what-heading what-heading--center">
            <p className="what-eyebrow">Our operating model</p>
            <h2>Focused support at every stage of growth.</h2>
            <p>
              Four group capabilities help our companies perform today and
              prepare responsibly for tomorrow.
            </p>
          </div>

          <div className="what-model__grid">
            {operatingModel.map(({ icon: Icon, title, text }, index) => (
              <article className="model-card" key={title}>
                <div className="model-card__top">
                  <Icon aria-hidden="true" />
                  <span>0{index + 1}</span>
                </div>
                <h3>{title}</h3>
                <p>{text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="what-services">
        <div className="what-shell">
          <div className="what-heading what-heading--split">
            <div>
              <p className="what-eyebrow">Where we operate</p>
              <h2>Services delivered through specialist companies.</h2>
            </div>
            <p>
              Customers engage directly with the Thrive business built for
              their specific need, supported by the strength of the group.
            </p>
          </div>

          <article className="service-panel service-panel--insurance">
            <div className="service-panel__identity">
              <span className="service-panel__icon">
                <FiShield aria-hidden="true" />
              </span>
              <p className="what-eyebrow">Insurance &amp; risk advisory</p>
              <div className="service-panel__logo">
                <img
                  src={insuranceLogo}
                  alt="Thrive Insurance Brokers Limited"
                />
              </div>
              <p>
                Helping individuals and organisations understand risk, secure
                appropriate cover and navigate claims with professional support.
              </p>
              <a
                href="https://www.insurance.thrivenig.com"
                target="_blank"
                rel="noopener noreferrer"
                className="what-text-link"
              >
                Explore insurance services <FiArrowRight aria-hidden="true" />
              </a>
            </div>
            <div className="service-panel__details">
              <h3>Core capabilities</h3>
              <ul>
                <li>
                  <FiCheck aria-hidden="true" /> Risk assessment and advisory
                </li>
                <li>
                  <FiCheck aria-hidden="true" /> Insurance programme design and
                  placement
                </li>
                <li>
                  <FiCheck aria-hidden="true" /> Competitive premium and policy
                  negotiation
                </li>
                <li>
                  <FiCheck aria-hidden="true" /> Cover administration and
                  renewals
                </li>
                <li>
                  <FiCheck aria-hidden="true" /> Claims processing and support
                </li>
              </ul>
            </div>
          </article>

          <article className="service-panel service-panel--travel">
            <div className="service-panel__identity">
              <span className="service-panel__icon">
                <ImAirplane aria-hidden="true" />
              </span>
              <p className="what-eyebrow">Travel management</p>
              <div className="service-panel__logo">
                <img
                  src={travelLogo}
                  alt="Thrive Travels and Tours Limited"
                />
              </div>
              <p>
                Making leisure and business travel easier through personalised
                planning, dependable reservations and responsive support.
              </p>
              <a
                href="https://www.travels.thrivenig.com"
                target="_blank"
                rel="noopener noreferrer"
                className="what-text-link"
              >
                Explore travel services <FiArrowRight aria-hidden="true" />
              </a>
            </div>
            <div className="service-panel__details">
              <h3>Core capabilities</h3>
              <ul>
                <li>
                  <FiCheck aria-hidden="true" /> Flight reservations and
                  ticketing
                </li>
                <li>
                  <FiCheck aria-hidden="true" /> Hotel and accommodation
                  planning
                </li>
                <li>
                  <FiCheck aria-hidden="true" /> Visa assistance
                </li>
                <li>
                  <FiCheck aria-hidden="true" /> Tailor-made holiday packages
                </li>
                <li>
                  <FiCheck aria-hidden="true" /> Corporate travel management
                </li>
              </ul>
            </div>
          </article>
        </div>
      </section>

      <section className="what-edge">
        <div
          className="what-edge__image"
          style={{ "--what-edge-image": `url(${peopleImage})` }}
          role="img"
          aria-label="Professionals collaborating around a table"
        />
        <div className="what-edge__content">
          <p className="what-eyebrow what-eyebrow--light">
            Our competitive edge
          </p>
          <h2>Professional judgement, backed by a shared standard.</h2>
          <p>
            Our advantage comes from combining the agility of focused
            businesses with the discipline and support of a wider group.
          </p>
          <div className="what-edge__points">
            <div>
              <FiLayers aria-hidden="true" />
              <span>
                <strong>Specialist focus</strong>
                Teams with practical knowledge of their markets.
              </span>
            </div>
            <div>
              <FiBarChart2 aria-hidden="true" />
              <span>
                <strong>Long-term thinking</strong>
                Decisions made for durable customer and business value.
              </span>
            </div>
            <div>
              <FiUsers aria-hidden="true" />
              <span>
                <strong>Responsive service</strong>
                Relationships built around clarity, access and follow-through.
              </span>
            </div>
          </div>
        </div>
      </section>

      <section className="what-cta">
        <div className="what-shell what-cta__inner">
          <div>
            <p className="what-eyebrow what-eyebrow--light">
              Work with Thrive
            </p>
            <h2>Tell us what you are trying to achieve.</h2>
          </div>
          <div>
            <p>
              We will connect you with the right team across the Thrive group.
            </p>
            <Link className="what-button what-button--white" to="/contact">
              Contact us <FiArrowRight aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Whatwedo;
